import React, { useId } from "react";
import { EDGE } from "../core/edgeTokens";
import { candleGeometry } from "./candleModel";

const TYPE_COLOR = { green: EDGE.green, red: EDGE.red, doji: EDGE.teal, zeroRange: EDGE.teal };

// Draws one candle from OHLC with the given scales. Pure SVG, no interaction;
// hit-testing is done in data units by the surface around it.
//
// highlight: { body?: bool, wicks?: bool } — gold emphasis (teaching / reveal).
export default function Candle({ ohlc, index = 0, scale, bodyHalf = 0.5, highlight = {} }) {
  const gid = useId().replace(/:/g, "");
  const g = candleGeometry(ohlc);
  const color = TYPE_COLOR[g.type];
  const cx = scale.x(index);
  const half = scale.x(index + bodyHalf) - cx;
  const top = scale.y(g.bodyTop);
  const bottom = scale.y(g.bodyBottom);
  const flat = g.bodyTop === g.bodyBottom;
  const bodyH = Math.max(bottom - top, flat ? 2.5 : 1);
  const bodyY = flat ? top - bodyH / 2 : top;

  return (
    <g>
      <defs>
        {/* Glossy, rounded body as on the reference board: a bright highlight
            band down the left third, darker toward the right edge, a light
            top edge and a soft shadow. The base colour stays the locked value;
            the sheen is white/black overlays on top of it. */}
        <linearGradient id={`sheen-${gid}`} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#000" stopOpacity="0.12" />
          {/* Highlight band cut ~25% from the first pass (0.22 / 0.42 / 0.08)
              at the client's request: polished, but an instrument, not a toy. */}
          <stop offset="0.12" stopColor="#fff" stopOpacity="0.165" />
          <stop offset="0.3" stopColor="#fff" stopOpacity="0.315" />
          <stop offset="0.48" stopColor="#fff" stopOpacity="0.06" />
          <stop offset="0.8" stopColor="#000" stopOpacity="0.06" />
          <stop offset="1" stopColor="#000" stopOpacity="0.26" />
        </linearGradient>
        <linearGradient id={`cap-${gid}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity="0.3" />
          <stop offset="0.08" stopColor="#fff" stopOpacity="0" />
          <stop offset="0.92" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.18" />
        </linearGradient>
        <filter id={`shadow-${gid}`} x="-40%" y="-10%" width="180%" height="130%">
          <feDropShadow dx="1.5" dy="2.5" stdDeviation="2.5" floodColor="#000" floodOpacity="0.18" />
        </filter>
      </defs>
      {highlight.wicks && g.hasUpperWick && (
        <line x1={cx} x2={cx} y1={scale.y(ohlc.high)} y2={top} stroke={EDGE.gold} strokeWidth={9} strokeLinecap="round" />
      )}
      {highlight.wicks && g.hasLowerWick && (
        <line x1={cx} x2={cx} y1={bottom} y2={scale.y(ohlc.low)} stroke={EDGE.gold} strokeWidth={9} strokeLinecap="round" />
      )}
      <line x1={cx} x2={cx} y1={scale.y(ohlc.high)} y2={scale.y(ohlc.low)} stroke={color} strokeWidth={2.4} />
      {highlight.body && (
        <rect
          x={cx - half - 4}
          y={bodyY - 4}
          width={half * 2 + 8}
          height={bodyH + 8}
          rx={6}
          fill="none"
          stroke={EDGE.gold}
          strokeWidth={3.5}
        />
      )}
      <rect x={cx - half} y={bodyY} width={half * 2} height={bodyH} rx={4} fill={color} filter={`url(#shadow-${gid})`} />
      {!flat && (
        <>
          <rect x={cx - half} y={bodyY} width={half * 2} height={bodyH} rx={4} fill={`url(#sheen-${gid})`} />
          <rect x={cx - half} y={bodyY} width={half * 2} height={bodyH} rx={4} fill={`url(#cap-${gid})`} />
        </>
      )}
    </g>
  );
}
